import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PublicPageShell from '../components/PublicPageShell';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { cmsApi } from '../lib/api';
import {
  Handshake, Storefront, Atom, Briefcase, MagnifyingGlass, Medal,
  Globe, ArrowRight, CheckCircle, UsersThree, ChartPieSlice, Star, PuzzlePiece,
} from '@phosphor-icons/react';

const PARTNER_TYPES = [
  {
    key: 'consulting',
    label: 'Consulting & SI',
    icon: Briefcase,
    tint: '#002677',
    desc: 'Big-4 consultancies and boutique firms guiding transformations end-to-end.',
    perks: ['Co-sell programme', 'Named TAM', 'Dedicated training'],
  },
  {
    key: 'reseller',
    label: 'Resellers & VARs',
    icon: Storefront,
    tint: '#00B140',
    desc: 'Regional partners selling BIL with their own services wrap.',
    perks: ['Margin tiers up to 35%', 'Co-branded assets', 'Deal registration'],
  },
  {
    key: 'isv',
    label: 'Technology / ISVs',
    icon: Atom,
    tint: '#8347AD',
    desc: 'Build on BIL APIs, publish to the BIL Marketplace, monetise per install.',
    perks: ['OAuth + webhooks', 'Marketplace listing', 'Revenue share 80/20'],
  },
  {
    key: 'advisory',
    label: 'M&A Advisors',
    icon: Handshake,
    tint: '#E4002B',
    desc: 'Investment banks, boutique advisors, and deal shops running on BIL rooms.',
    perks: ['Volume discounts', 'White-label rooms', 'Priority support'],
  },
];

const PARTNER_BENEFITS = [
  { icon: UsersThree,      label: 'Joint GTM',        desc: 'Account mapping + co-marketing budgets.' },
  { icon: ChartPieSlice,   label: 'Revenue share',    desc: 'Up to 35% on resale, 20% on marketplace apps.' },
  { icon: Medal,           label: 'Certifications',   desc: 'BIL Certified Consultant, Architect, AI Specialist.' },
  { icon: PuzzlePiece,     label: 'APIs & SDKs',      desc: 'Full REST, webhooks, React/JS + Python SDK.' },
  { icon: Globe,           label: 'Global network',   desc: '210+ partners across 38 countries.' },
  { icon: Star,            label: 'Partner of year',  desc: 'Annual awards + invitation to BIL Exec Council.' },
];

const DEFAULT_FEATURED_PARTNERS = [
  { name: 'Nordlight Advisors', region: 'EU · Nordic M&A',         tier: 'Gold' },
  { name: 'Helix Consulting',   region: 'UK · Healthcare',         tier: 'Platinum' },
  { name: 'Meridian Digital',   region: 'APAC · Retail / SaaS',    tier: 'Gold' },
  { name: 'Atlas Group',        region: 'LATAM · Financial Services', tier: 'Silver' },
  { name: 'Lumen Advisory',     region: 'NA · Energy',             tier: 'Platinum' },
  { name: 'Beacon Partners',    region: 'MENA · Public Sector',    tier: 'Gold' },
];

const TIER_STYLES = {
  Platinum: 'bg-[#002677]/10 text-[#002677]',
  Gold:     'bg-[#FFD100]/25 text-[#B5940A]',
  Silver:   'bg-[#6B7280]/15 text-[#4B5563]',
};

export default function PartnersPage() {
  const [q, setQ] = useState('');
  const [partners, setPartners] = useState(DEFAULT_FEATURED_PARTNERS);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await cmsApi.listPublic('partner');
        if (cancelled) return;
        if (Array.isArray(data) && data.length > 0) {
          setPartners(data.map((i) => i.data || {}));
        }
      } catch { /* keep defaults */ }
    })();
    return () => { cancelled = true; };
  }, []);
  const filtered = partners.filter((p) =>
    !q || (p.name || '').toLowerCase().includes(q.toLowerCase()) || (p.region || '').toLowerCase().includes(q.toLowerCase())
  );
  return (
    <PublicPageShell
      testId="partners-page"
      eyebrow="Partners"
      title="Grow with the BIL ecosystem."
      subtitle="A global network of consultancies, resellers and technology partners bringing BIL to regulated industries worldwide."
      ctaPrimary={{ label: 'Become a partner', to: '/register?ref=partner' }}
      ctaSecondary={{ label: 'Find a partner', to: '#find-partner' }}
    >
      {/* Partner types */}
      <section className="py-16 lg:py-20 bg-background" id="partner-types">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#002677] mb-3">
              Programmes
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">
              Four ways to partner with BIL.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="partner-types-grid">
            {PARTNER_TYPES.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.key}
                  className="bg-card border border-border rounded-sm p-6 hover:border-foreground/30 hover:shadow-lg transition-all flex flex-col"
                  data-testid={`partner-type-${p.key}`}
                >
                  <div
                    className="h-11 w-11 rounded-sm flex items-center justify-center mb-4"
                    style={{ background: `${p.tint}1A` }}
                  >
                    <Icon size={22} weight="duotone" color={p.tint} />
                  </div>
                  <h3 className="font-heading text-lg font-semibold tracking-tight">{p.label}</h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed flex-1">
                    {p.desc}
                  </p>
                  <ul className="mt-4 space-y-1.5">
                    {p.perks.map((perk, i) => (
                      <li key={i} className="flex items-start gap-2 text-[13px]">
                        <CheckCircle size={13} weight="fill" className="flex-shrink-0 mt-0.5" style={{ color: p.tint }} />
                        <span className="text-foreground/90">{perk}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits grid */}
      <section className="py-16 lg:py-20 bg-[#f5f6fa] dark:bg-muted/20 border-y border-border">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#002677] mb-3">
              Why partner
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">
              Built to scale alongside your practice.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="partner-benefits">
            {PARTNER_BENEFITS.map((b) => {
              const Icon = b.icon;
              return (
                <div
                  key={b.label}
                  className="bg-card border border-border rounded-sm p-5 flex items-start gap-4"
                  data-testid={`benefit-${b.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="h-10 w-10 rounded-sm bg-[#002677]/10 flex items-center justify-center flex-shrink-0">
                    <Icon size={20} weight="duotone" className="text-[#002677]" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">{b.label}</p>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{b.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Built with — Technology partners */}
      <section className="py-14 bg-background" data-testid="built-with-section">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#002677] mb-3">
              Built with
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">
              The platform behind BIL.
            </h2>
            <p className="text-sm text-muted-foreground mt-3 max-w-xl mx-auto">
              BIL is built, shipped and scaled on the Emergent platform — our core technology partner.
            </p>
          </div>
          <a
            href="https://app.emergent.sh/?utm_source=bil-partners-page"
            target="_blank"
            rel="noopener noreferrer"
            className="block bg-card border border-border rounded-sm p-6 md:p-8 hover:border-foreground/30 hover:shadow-lg transition-all"
            data-testid="emergent-partner-card"
          >
            <div className="flex flex-col md:flex-row md:items-center gap-5">
              <div className="flex-shrink-0 h-14 w-14 rounded-sm bg-black flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 16 16" fill="none">
                  <path d="M15.5702 8.13142C15.7729 8.0412 16.0007 8.18878 15.9892 8.4103C15.8374 11.3192 14.0965 14.0405 11.2531 15.3065C8.40964 16.5725 5.2224 16.0453 2.95912 14.2117C2.78676 14.072 2.82955 13.804 3.03219 13.7137L4.95677 12.8568C5.04866 12.8159 5.15446 12.823 5.24204 12.8725C6.73377 13.7153 8.59176 13.8649 10.2772 13.1145C11.9626 12.3641 13.0947 10.8833 13.4665 9.21075C13.4883 9.11256 13.5539 9.02918 13.6457 8.98827L15.5702 8.13142Z" fill="white"/>
                  <path fillRule="evenodd" clipRule="evenodd" d="M15.3066 4.74698L15.5067 5.19653C15.5759 5.35178 15.5061 5.53366 15.3508 5.60278L1.29992 11.8586C1.14467 11.9278 0.962794 11.8579 0.893675 11.7027L0.701732 11.2716L0.693457 11.2531C-1.10317 7.21778 0.711626 2.49007 4.74692 0.693443C8.78221 -1.10318 13.51 0.711693 15.3066 4.74698ZM2.82356 8.55367C2.63552 8.63739 2.41991 8.51617 2.40853 8.31065C2.28373 6.05724 3.53858 3.85787 5.72286 2.88536C7.90715 1.91286 10.3813 2.45199 11.9724 4.05256C12.1175 4.19854 12.0633 4.43988 11.8753 4.5236L2.82356 8.55367Z" fill="white"/>
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-heading text-xl font-semibold tracking-tight">Emergent</h3>
                  <span className="text-[10px] tracking-[0.2em] uppercase font-bold px-2 py-0.5 rounded-full bg-black text-white">
                    Core tech partner
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
                  The full-stack AI platform that lets teams build, ship and scale production apps.
                  BIL runs end-to-end on Emergent — Made with Emergent.
                </p>
              </div>
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground flex-shrink-0">
                Visit emergent.sh <ArrowRight size={14} weight="bold" />
              </span>
            </div>
          </a>
        </div>
      </section>

      {/* Find a partner */}
      <section className="py-16 lg:py-20 bg-background" id="find-partner">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-8">
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#002677] mb-3">
              Directory
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">
              Find a partner near you.
            </h2>
          </div>
          <div className="max-w-lg mx-auto mb-8">
            <div className="relative">
              <MagnifyingGlass
                size={18}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name, region, or industry"
                className="pl-11 h-12 rounded-full"
                data-testid="partner-search"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="featured-partners">
            {filtered.length === 0 ? (
              <div className="col-span-full text-center py-10 border border-dashed border-border rounded-sm bg-muted/10">
                <p className="text-sm text-muted-foreground">No partners match &ldquo;{q}&rdquo; yet.</p>
                <Link to="/register?ref=partner" className="text-[#002677] text-sm font-semibold mt-2 inline-flex items-center gap-1.5">
                  Become the first <ArrowRight size={14} weight="bold" />
                </Link>
              </div>
            ) : (
              filtered.map((p) => (
                <div
                  key={p.name}
                  className="bg-card border border-border rounded-sm p-5 hover:border-[#002677] hover:shadow-md transition-all"
                  data-testid={`partner-card-${p.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-heading font-semibold text-base tracking-tight">{p.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{p.region}</p>
                    </div>
                    <span className={`text-[10px] tracking-[0.18em] uppercase font-bold px-2 py-1 rounded-full ${TIER_STYLES[p.tier]}`}>
                      {p.tier}
                    </span>
                  </div>
                  <Link
                    to="/register"
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#002677] hover:gap-2.5 transition-all"
                  >
                    Contact partner <ArrowRight size={13} weight="bold" />
                  </Link>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Apply to partner program */}
      <section className="py-16 bg-[#f5f6fa] dark:bg-muted/20 border-t border-border">
        <div className="max-w-[1100px] mx-auto px-6">
          <div className="bg-[#002677] rounded-sm p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6" data-testid="partners-apply">
            <div className="text-white max-w-xl">
              <h3 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">
                Ready to join?
              </h3>
              <p className="mt-2 text-white/80 text-sm sm:text-base">
                Apply to the BIL Partner Programme. We onboard in under 10 business days.
              </p>
            </div>
            <Button
              asChild
              size="lg"
              className="rounded-sm font-medium h-12 px-7 bg-white text-[#002677] hover:bg-white/90"
              data-testid="partners-apply-cta"
            >
              <Link to="/register?ref=partner">Apply now <ArrowRight size={16} className="ml-2" weight="bold" /></Link>
            </Button>
          </div>
        </div>
      </section>
    </PublicPageShell>
  );
}
